import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, MailWarning, X } from "lucide-react";
import { Button, toast } from "@atrix-analytics/ui";
import { api } from "../api";
import { errorMessage } from "../api/http";
import { useWorkspace } from "../lib/workspace";
import { DemoMailLink } from "../routes/recovery";

const DISMISS_KEY = "atx-verify-banner-dismissed";

export function ResendVerificationButton() {
  const [sent, setSent] = useState(false);
  const resend = useMutation({
    mutationFn: () => api.resendVerification(),
    onSuccess: () => {
      setSent(true);
      toast.success("Verification email sent. The link works for 3 days.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="flex flex-col gap-2">
      <Button variant="secondary" size="sm" className="self-start" onClick={() => resend.mutate()} disabled={resend.isPending}>
        {resend.isPending && <Loader2 className="animate-spin" aria-hidden />}
        {sent ? "Send it again" : "Resend verification email"}
      </Button>
      <DemoMailLink kind="verify_email" key={String(sent)} />
    </div>
  );
}

/** Nudge to verify the account email. Dismissable for this browser session, never blocking. */
export function VerifyBanner() {
  const { me } = useWorkspace();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === me.user.id;
    } catch {
      return false;
    }
  });
  if (me.user.email_verified || dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, me.user.id);
    } catch {
      /* private mode — it just comes back next load */
    }
  };
  return (
    <aside
      aria-label="Verify your email"
      className="mb-5 flex items-start gap-3 rounded-lg border border-warn/30 bg-warn/8 px-4 py-3 text-sm"
    >
      <MailWarning aria-hidden className="mt-0.5 size-4 shrink-0 text-warn" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">Verify your email address</p>
        <p className="mt-0.5 text-fg-muted">
          We sent a link to <span className="break-all text-fg">{me.user.email}</span>. Verified addresses get security
          notices and can invite teammates.
        </p>
        <div className="mt-2">
          <ResendVerificationButton />
        </div>
      </div>
      <Button variant="ghost" size="icon" className="-mt-1 -mr-2 size-8" aria-label="Dismiss for now" onClick={dismiss}>
        <X />
      </Button>
    </aside>
  );
}
